import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { extractPlasmicQueryData, PlasmicComponent, PlasmicRootProvider } from '@plasmicapp/loader-nextjs';
import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { SeoOverrides } from '~/components/Seo/SeoOverrides';
import Layout from '~/components/shared/Layout';
import Link from '~/components/shared/Link';
import { NavbarElement } from '~/components/shared/Navbar/types';
import Errors from '~/helpers/errors';
import { getSeoOverrides, PLASMIC, PlasmicProps, revalidateInterval } from '~/helpers/plasmic';
import Config from '~/lib/config';
import { getTranslations } from '~/lib/next-i18next';

const { Typography } = ToucanComponents;

const PlasmicAbout = ({ plasmicData, plasmicPath, queryCache }: Required<PlasmicProps>) => {
  const { query } = useRouter();
  const [pageMeta] = plasmicData.entryCompMetas;
  const overrides = getSeoOverrides(plasmicData);
  return (
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      prefetchedQueryData={queryCache}
      pageParams={pageMeta.params}
      pageQuery={query}
    >
      <Head>
        <link rel="canonical" href={Config.SITE + plasmicPath} />
      </Head>
      <SeoOverrides {...overrides} />
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>
  );
};

const StandardAbout = () => {
  const theme = useTheme();
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const { t } = useTranslation('about');
  const pageDescription = t('aboutMetaDescription');

  return (
    <>
      <SeoOverrides
        title={t('aboutMetaTitle')}
        description={pageDescription}
        openGraph={{
          title: t('aboutMetaTitle'),
          description: pageDescription,
        }}
      />
      <Layout
        pageError={Errors.AboutPageError}
        navbarVisibleElements={[
          NavbarElement.HomeLink,
          NavbarElement.AddForFreeButton,
          NavbarElement.DashboardLink,
          NavbarElement.DisplayLanguageSwitcherButton,
        ]}
      >
        <div
          css={css`
            padding: ${theme.spacing(10)} 0;
            display: flex;
            justify-content: center;
          `}
        >
          <div
            css={css`
              max-width: 684px;
            `}
          >
            <Typography
              variant={isViewportAboveSm ? 'xl' : 'lg'}
              font="secondary"
              css={css`
                margin-bottom: ${theme.spacing(7)};
              `}
            >
              {t('aboutToucan')}
            </Typography>

            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutToucanNewImmersiveWay')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutToucanLearning')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutToucanHelpsYou')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutWhenYouVisit')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutLearningNewSkill')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutWeHopeYouEnjoy')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(4)};
              `}
            >
              {t('aboutToucanTeam')}
            </Typography>

            <div
              css={css`
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                ${theme.breakpoints.up('sm')} {
                  flex-direction: row;
                  justify-content: space-between;
                }
              `}
            >
              <div
                css={css`
                  margin-bottom: ${theme.spacing(6)};
                  text-align: center;
                  ${theme.breakpoints.up('sm')} {
                    margin-bottom: 0;
                    padding-left: ${theme.spacing(3)};
                  }
                `}
              >
                <img
                  src="/images/about/tn-photo.png"
                  alt="Taylor Nieman"
                  css={css`
                    width: 144px;
                    height: 144px;
                    margin-bottom: ${theme.spacing(3)};
                  `}
                />
                <Typography
                  variant="sm"
                  isBold
                  css={css`
                    margin-bottom: ${theme.spacing(1)};
                  `}
                >
                  Taylor Nieman
                </Typography>
                <Typography
                  variant="sm"
                  css={css`
                    margin-bottom: ${theme.spacing(1)};
                  `}
                >
                  {t('titleAndTitle', {
                    firstTitle: 'Co-Founder',
                    secondTitle: 'CEO',
                  })}
                </Typography>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 40px;
                  `}
                >
                  <Link href="https://twitter.com/Taylor_Nieman">
                    <img
                      src="/images/about/twitter.svg"
                      alt="Twitter"
                      css={css`
                        width: 24px;
                        height: 24px;
                        margin-right: ${theme.spacing(4)};
                      `}
                    />
                  </Link>
                  <Link href="https://www.linkedin.com/in/taylornieman/">
                    <img
                      src="/images/about/linkedin.svg"
                      alt="LinkedIn"
                      css={css`
                        width: 24px;
                        height: 24px;
                        margin-right: ${theme.spacing(4)};
                      `}
                    />
                  </Link>
                  <a target="_blank" href="mailto:taylor.nieman@jointoucan.com" rel="noreferrer noopener">
                    <img
                      src="/images/about/envelope.svg"
                      alt="Email"
                      css={css`
                        width: 24px;
                        height: 24px;
                      `}
                    />
                  </a>
                </div>
              </div>

              <div
                css={css`
                  margin-bottom: ${theme.spacing(6)};
                  text-align: center;
                  ${theme.breakpoints.up('sm')} {
                    margin-bottom: 0;
                  }
                `}
              >
                <img
                  src="/images/about/sm-photo.png"
                  alt="Shaun Merritt"
                  css={css`
                    width: 144px;
                    height: 144px;
                    margin-bottom: ${theme.spacing(3)};
                  `}
                />
                <Typography
                  variant="sm"
                  isBold
                  css={css`
                    margin-bottom: ${theme.spacing(1)};
                  `}
                >
                  Shaun Merritt
                </Typography>
                <Typography
                  variant="sm"
                  css={css`
                    margin-bottom: ${theme.spacing(1)};
                  `}
                >
                  {t('titleAndTitle', {
                    firstTitle: 'Co-Founder',
                    secondTitle: 'CTO',
                  })}
                </Typography>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 40px;
                  `}
                >
                  <Link href="https://twitter.com/ShaunMerritt_">
                    <img
                      src="/images/about/twitter.svg"
                      alt="Twitter"
                      css={css`
                        width: 24px;
                        height: 24px;
                        margin-right: ${theme.spacing(4)};
                      `}
                    />
                  </Link>
                  <Link href="https://www.linkedin.com/in/shaun-merritt-566214aa/">
                    <img
                      src="/images/about/linkedin.svg"
                      alt="LinkedIn"
                      css={css`
                        width: 24px;
                        height: 24px;
                      `}
                    />
                  </Link>
                </div>
              </div>

              <div
                css={css`
                  text-align: center;
                  ${theme.breakpoints.up('sm')} {
                    padding-right: ${theme.spacing(3)};
                  }
                `}
              >
                <img
                  src="/images/about/bd-photo.png"
                  alt="Brandon Dietz"
                  css={css`
                    width: 144px;
                    height: 144px;
                    margin-bottom: ${theme.spacing(3)};
                  `}
                />
                <Typography
                  variant="sm"
                  isBold
                  css={css`
                    margin-bottom: ${theme.spacing(1)};
                  `}
                >
                  Brandon Dietz
                </Typography>
                <Typography
                  variant="sm"
                  css={css`
                    margin-bottom: ${theme.spacing(1)};
                  `}
                >
                  {t('titleAndTitle', {
                    firstTitle: 'Co-Founder',
                    secondTitle: 'CPO',
                  })}
                </Typography>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 40px;
                  `}
                >
                  <Link href="https://twitter.com/BrandonDietz_">
                    <img
                      src="/images/about/twitter.svg"
                      alt="Twitter"
                      css={css`
                        width: 24px;
                        height: 24px;
                        margin-right: ${theme.spacing(4)};
                      `}
                    />
                  </Link>
                  <Link href="https://www.linkedin.com/in/brandondietz/">
                    <img
                      src="/images/about/linkedin.svg"
                      alt="LinkedIn"
                      css={css`
                        width: 24px;
                        height: 24px;
                      `}
                    />
                  </Link>
                </div>
              </div>
            </div>

            <div
              css={css`
                height: 1px;
                width: 100%;
                background-color: ${ToucanColors.gray[100]};
                margin: ${theme.spacing(7)} 0;
              `}
            />

            <Typography
              variant="md"
              isBold
              css={css`
                margin-bottom: ${theme.spacing(5)};
              `}
            >
              {t('aboutBackedByScience')}
            </Typography>
            <Typography
              variant="sm"
              css={css`
                margin-bottom: ${theme.spacing(3)};
              `}
            >
              {t('aboutToucanIsBuilt')}
            </Typography>
            <Typography variant="sm">{t('aboutWeSupercharge')}</Typography>

            <div
              css={css`
                height: 1px;
                width: 100%;
                background-color: ${ToucanColors.gray[100]};
                margin: ${theme.spacing(7)} 0;
              `}
            />

            <Typography
              variant="md"
              isBold
              css={css`
                margin-bottom: ${theme.spacing(5)};
              `}
            >
              {t('aboutTranslatedBy')}
            </Typography>
            <Typography variant="sm">{t('aboutAllTranslations')}</Typography>

            <div
              css={css`
                height: 1px;
                width: 100%;
                background-color: ${ToucanColors.gray[100]};
                margin: ${theme.spacing(7)} 0;
              `}
            />

            <Typography
              variant="md"
              isBold
              css={css`
                margin-bottom: ${theme.spacing(5)};
              `}
            >
              {t('aboutPrivateAndSecure')}
            </Typography>
            <Typography variant="sm">{t('aboutToucanCares')}</Typography>

            <div
              css={css`
                height: 1px;
                width: 100%;
                background-color: ${ToucanColors.gray[100]};
                margin: ${theme.spacing(7)} 0;
              `}
            />
          </div>
        </div>
      </Layout>
    </>
  );
};

const About = ({ plasmicData, plasmicPath, queryCache }: PlasmicProps) =>
  plasmicData && plasmicPath && queryCache ? (
    <PlasmicAbout plasmicData={plasmicData} plasmicPath={plasmicPath} queryCache={queryCache} />
  ) : (
    <StandardAbout />
  );

export default About;

const fetchPlasmicProps = async (locale?: string) => {
  // The Plasmic page isn't localized.
  if (locale !== 'en') {
    return {};
  }
  // Statically fetch the data needed to render the Plasmic page.
  const plasmicData = await PLASMIC.maybeFetchComponentData('/about');
  if (!plasmicData) {
    return {};
  }
  const [pageMeta] = plasmicData.entryCompMetas;
  // Cache the necessary data fetched for the page.
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider loader={PLASMIC} prefetchedData={plasmicData} pageParams={pageMeta.params}>
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>,
  );
  return { plasmicData, queryCache };
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const plasmicProps = await fetchPlasmicProps(locale);
  return {
    props: {
      ...(await getTranslations({
        locale,
        namespaces: 'about',
      })),
      ...plasmicProps,
      plasmicPath: '/about',
    },
    revalidate: revalidateInterval,
  };
};
